import api from "@/lib/api";

/**
 * Studio v2 API client. Thin wrappers around /api/studio/* so pages stay readable.
 * All producer calls require the `producer_tools` capability (session token is
 * attached by the shared axios instance).
 */

// One id per browser: the console that holds "control" of a show sends it on every request,
// so a second laptop can't cut / tag / record over the producer in charge.
export function producerClientId() {
  if (typeof window === "undefined") return "server";
  let id = localStorage.getItem("fc_producer_client");
  if (!id) {
    id = `pc_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem("fc_producer_client", id);
  }
  return id;
}
api.defaults.headers.common["X-Producer-Client"] = producerClientId();

export function deviceLabel() {
  if (typeof navigator === "undefined") return "computer";
  const ua = navigator.userAgent || "";
  const os = /iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) ? "iPad" : /iPhone/.test(ua) ? "iPhone" : /Android/.test(ua) ? "Android" : /Mac OS/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "computer";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "browser";
  return `${os} · ${browser}`;
}

export const studioApi = {
  status: () => api.get("/studio/status").then((r) => r.data),

  // Shows
  listShows: (status) => api.get("/studio/shows", { params: status ? { status } : {} }).then((r) => r.data),
  createShow: (body) => api.post("/studio/shows", body).then((r) => r.data),
  getShow: (showId) => api.get(`/studio/shows/${showId}`).then((r) => r.data),
  patchShow: (showId, body) => api.patch(`/studio/shows/${showId}`, body).then((r) => r.data),
  deleteShow: (showId) => api.delete(`/studio/shows/${showId}`).then((r) => r.data),
  hostToken: (showId, viewOnly = false) => api.post(`/studio/shows/${showId}/host-token`, { view_only: !!viewOnly }).then((r) => r.data),
  endShow: (showId) => api.post(`/studio/shows/${showId}/end`).then((r) => r.data),
  refresh: (showId) => api.post(`/studio/shows/${showId}/refresh`).then((r) => r.data),

  // Producer control (one computer produces at a time)
  claimControl: (showId, force = false) => api.post(`/studio/shows/${showId}/control/claim`, { client_id: producerClientId(), device: deviceLabel(), force }).then((r) => r.data),
  controlHeartbeat: (showId) => api.post(`/studio/shows/${showId}/control/heartbeat`, { client_id: producerClientId() }).then((r) => r.data),
  releaseControl: (showId) => api.post(`/studio/shows/${showId}/control/release`, { client_id: producerClientId() }).then((r) => r.data),
  listMonitors: (showId) => api.get(`/studio/shows/${showId}/monitors`).then((r) => r.data),

  // Program state
  updateProgram: (showId, patch) => api.put(`/studio/shows/${showId}/program`, patch).then((r) => r.data),
  broadcastDelay: (showId) => api.get(`/studio/shows/${showId}/broadcast-delay`).then((r) => r.data),

  // Room monitor (venue TVs) — secret link, no sign-in
  monitorData: (showId, key) => api.get(`/studio/monitor/${showId}/${key}`).then((r) => r.data),
  monitorToken: (showId, key) => api.post(`/studio/monitor/${showId}/${key}/token`).then((r) => r.data),
  monitorBeacon: (showId, key, body) => api.post(`/studio/monitor/${showId}/${key}/beacon`, body).then((r) => r.data),
  rotateMonitorKey: (showId) => api.post(`/studio/shows/${showId}/monitor-key/rotate`).then((r) => r.data),

  // Saved name plates (library shared across shows)
  listPlates: (q) => api.get("/studio/plates", { params: q ? { q } : {} }).then((r) => r.data),
  savePlate: (body) => api.post("/studio/plates", body).then((r) => r.data),
  deletePlate: (plateId) => api.delete(`/studio/plates/${plateId}`).then((r) => r.data),

  // Participants
  patchParticipant: (showId, pid, body) => api.patch(`/studio/shows/${showId}/participants/${pid}`, body).then((r) => r.data),
  micControl: (showId, participantIds, enabled) => api.post(`/studio/shows/${showId}/mic`, { participant_ids: participantIds, enabled }).then((r) => r.data),
  admit: (showId, pid) => api.post(`/studio/shows/${showId}/participants/${pid}/admit`).then((r) => r.data),
  decline: (showId, pid) => api.post(`/studio/shows/${showId}/participants/${pid}/decline`).then((r) => r.data),
  remove: (showId, pid) => api.post(`/studio/shows/${showId}/participants/${pid}/remove`).then((r) => r.data),

  // Jobs
  startRecording: (showId) => api.post(`/studio/shows/${showId}/recording/start`, { program_base_url: window.location.origin }).then((r) => r.data),
  stopRecording: (showId) => api.post(`/studio/shows/${showId}/recording/stop`).then((r) => r.data),
  startLive: (showId) => api.post(`/studio/shows/${showId}/live/start`, { program_base_url: window.location.origin }).then((r) => r.data),
  stopLive: (showId) => api.post(`/studio/shows/${showId}/live/stop`).then((r) => r.data),

  // Audience questions (Hub Q&A) -> on air
  listQuestions: (showId) => api.get(`/studio/shows/${showId}/questions`).then((r) => r.data),
  airQuestion: (showId, qid) => api.post(`/studio/shows/${showId}/questions/${qid}/air`).then((r) => r.data),
  clearQuestion: (showId) => api.post(`/studio/shows/${showId}/questions/clear`).then((r) => r.data),
  setQuestionStatus: (showId, qid, status) => api.post(`/studio/shows/${showId}/questions/${qid}/status`, { status }).then((r) => r.data),

  // Public join flows
  joinInfo: (token) => api.get(`/studio/join/${token}`).then((r) => r.data),
  joinRequest: (token, body) => api.post(`/studio/join/${token}`, body).then((r) => r.data),
  joinStatus: (token, pid, key) => api.get(`/studio/join/${token}/status/${pid}`, { params: { key } }).then((r) => r.data),
  cameraInfo: (token) => api.get(`/studio/cam/${token}`).then((r) => r.data),
  cameraJoin: (token, body) => api.post(`/studio/cam/${token}`, body).then((r) => r.data),

  // Program output page
  programData: (showId, key) => api.get(`/studio/program/${showId}/${key}`).then((r) => r.data),
  previewToken: (showId, key) => api.post(`/studio/program/${showId}/${key}/preview-token`).then((r) => r.data),
  programEvent: (showId, key, body) => api.post(`/studio/program/${showId}/${key}/event`, body).then((r) => r.data),
};

export function errMessage(e, fallback = "Something went wrong") {
  return e?.response?.data?.detail || e?.message || fallback;
}

export default studioApi;
